import { Controller } from "@hotwired/stimulus";

// Matches the `media-breakpoint-down(lg)` cutoff used in _trix_editor.scss.
const FULLSCREEN_MEDIA_QUERY = "(max-width: 991.98px)";
const HISTORY_FLAG = "trixFullscreen";

// On phones, focusing the editor expands it to fill the visible screen. The
// overlay tracks the visual viewport so it ends at the top of the virtual
// keyboard, and a history entry is pushed so the back gesture closes it.
export default class extends Controller {
  static targets = ["editor"];

  connect() {
    this.active = false;
    this.historyPushed = false;
    this.awaitingDonePop = false;
    // Tags this editor's history marker, so it only reacts to its own.
    this.markerId = `${Date.now()}-${Math.random()}`;

    this.onFocus = this.onFocus.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.exit = this.exit.bind(this);
    this.fitToViewport = this.fitToViewport.bind(this);

    this.element.addEventListener("trix-focus", this.onFocus);
    window.addEventListener("popstate", this.onPopState);
    // Turbo's cached copy of the page must never be in fullscreen.
    document.addEventListener("turbo:before-cache", this.exit);
  }

  disconnect() {
    this.element.removeEventListener("trix-focus", this.onFocus);
    window.removeEventListener("popstate", this.onPopState);
    document.removeEventListener("turbo:before-cache", this.exit);
    this.exit();
  }

  onFocus() {
    if (window.matchMedia(FULLSCREEN_MEDIA_QUERY).matches) this.enter();
  }

  done(event) {
    event?.preventDefault();
    this.editorTarget.blur();
    this.exit();
    if (this.historyPushed) {
      this.awaitingDonePop = true;
      history.back();
    }
  }

  enter() {
    if (this.active) return;

    this.active = true;
    this.element.classList.add("trix-field--fullscreen");
    document.body.classList.add("trix-fullscreen-active");
    window.visualViewport?.addEventListener("resize", this.fitToViewport);
    window.visualViewport?.addEventListener("scroll", this.fitToViewport);
    this.fitToViewport();
    this.pushHistoryEntry();
  }

  exit() {
    if (!this.active) return;

    this.active = false;
    this.element.classList.remove("trix-field--fullscreen");
    document.body.classList.remove("trix-fullscreen-active");
    window.visualViewport?.removeEventListener("resize", this.fitToViewport);
    window.visualViewport?.removeEventListener("scroll", this.fitToViewport);
    this.element.style.removeProperty("top");
    this.element.style.removeProperty("height");
  }

  // Leaving our history entry: either the back gesture (close fullscreen) or
  // the history.back() that Done issued to drop the entry it no longer needs.
  onPopState(event) {
    if (!this.historyPushed || event.state?.[HISTORY_FLAG] !== this.markerId) return;

    this.historyPushed = false;

    if (this.awaitingDonePop) {
      this.awaitingDonePop = false;
      // The editor was reopened before the pop landed: it needs its own entry.
      if (this.active) this.pushHistoryEntry();
    } else {
      this.exit();
    }
  }

  pushHistoryEntry() {
    if (this.historyPushed) return;

    // The current entry is swapped for a marker without Turbo's state, and
    // Turbo's state moves onto the pushed entry (so a visit away and back still
    // restores the page). Turbo doesn't visit on a pop onto the marker: its
    // own popstate listener, which runs first, just gives the entry fresh state.
    const turboState = history.state;
    history.replaceState({ [HISTORY_FLAG]: this.markerId }, "");
    history.pushState(turboState, "");
    this.historyPushed = true;
  }

  fitToViewport() {
    const viewport = window.visualViewport;
    if (!viewport) return;

    this.element.style.top = `${viewport.offsetTop}px`;
    this.element.style.height = `${viewport.height}px`;
  }
}
